import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, ExternalLink, KeyRound, ShieldAlert } from 'lucide-react';
import { api } from '../lib/api';
import VerificationBreadcrumb from '../components/VerificationBreadcrumb';

const labClaims = [
  ['identity_verified', 'Identity verified'],
  ['full_name_verified', 'Full name verified'],
  ['email_verified', 'Email verified'],
  ['document_verified', 'Document verified'],
  ['digital_key_account_active', 'Digital Key Account active'],
  ['device_bound', 'Device bound'],
  ['internal_assurance_policy', 'Authentication policy status'],
];

const statusStyles = {
  pending: 'border-amber-300 bg-amber-50 text-amber-800',
  login_request_created: 'border-zinc-200 bg-zinc-50 text-zinc-700',
  code_issued: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  token_exchanged: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  expired: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  cancelled: 'border-zinc-200 bg-zinc-50 text-zinc-600',
};

const flowToneStyles = {
  ready: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  pending: 'border-amber-200 bg-amber-50 text-amber-800',
  neutral: 'border-zinc-200 bg-zinc-50 text-zinc-600',
  active: 'border-sky-200 bg-sky-50 text-sky-800',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

function decodeJwtPart(token, index) {
  try {
    const part = String(token || '').split('.')[index];
    if (!part) return null;
    const normalized = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(window.atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=')));
  } catch {
    return null;
  }
}

function LabStatus({ status }) {
  return <span className={`inline-flex border px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-widest ${statusStyles[status] || statusStyles.pending}`}>{String(status || 'pending').replaceAll('_', ' ')}</span>;
}

function FlowCard({ title, value, detail, tone = 'neutral' }) {
  return (
    <div className={`border p-4 ${flowToneStyles[tone] || flowToneStyles.neutral}`}>
      <p className="text-[10px] font-mono uppercase tracking-widest opacity-75">{title}</p>
      <p className="mt-2 text-sm font-medium">{value}</p>
      <p className="mt-1 text-xs opacity-80">{detail}</p>
    </div>
  );
}

export default function OidcLabSimulator() {
  const [searchParams] = useSearchParams();
  const prefilledIdentityId = searchParams.get('identity_id') || searchParams.get('identity') || '';
  const prefilledAccountId = searchParams.get('account_id') || searchParams.get('account') || '';
  const prefilledRequestId = searchParams.get('request_id') || '';
  const hasPrefill = Boolean(prefilledIdentityId || prefilledAccountId);
  const hasReturnedRequest = Boolean(prefilledRequestId);
  const [identityId, setIdentityId] = useState(prefilledIdentityId);
  const [accountId, setAccountId] = useState(prefilledAccountId);
  const [requestedAssurance, setRequestedAssurance] = useState('internal_authentication_ready');
  const [requestedClaims, setRequestedClaims] = useState(['identity_verified', 'document_verified', 'digital_key_account_active', 'device_bound', 'internal_assurance_policy']);
  const [labRequest, setLabRequest] = useState(null);
  const [consumerRequestUrl, setConsumerRequestUrl] = useState('');
  const [recentRequests, setRecentRequests] = useState([]);
  const [authorizationCode, setAuthorizationCode] = useState('');
  const [redirectUri, setRedirectUri] = useState('');
  const [idToken, setIdToken] = useState('');
  const [userinfo, setUserinfo] = useState(null);
  const [claimRelease, setClaimRelease] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const handledReturnedRequestRef = useRef(false);

  const loadRecent = useCallback(async () => {
    const { data } = await api.get('/oidc-lab/admin/oidc-lab/requests?limit=20');
    setRecentRequests(data.requests || []);
  }, []);

  useEffect(() => {
    loadRecent().catch(() => setNotice({ tone: 'error', text: 'Recent OIDC lab requests could not be loaded.' }));
  }, [loadRecent]);

  useEffect(() => {
    if (prefilledIdentityId) setIdentityId(prefilledIdentityId);
    if (prefilledAccountId) setAccountId(prefilledAccountId);
  }, [prefilledAccountId, prefilledIdentityId]);

  useEffect(() => {
    if (!prefilledRequestId || handledReturnedRequestRef.current || recentRequests.length === 0) return;
    const matchedRequest = recentRequests.find((request) => request.login_request_id === prefilledRequestId || request.id === prefilledRequestId);
    if (matchedRequest) {
      handledReturnedRequestRef.current = true;
      setLabRequest(matchedRequest);
      setAuthorizationCode('');
      setRedirectUri('');
      setIdToken('');
      setUserinfo(null);
      setClaimRelease(null);
      setNotice({ tone: 'success', text: 'Ready to finalize returned request from Authenticator Preview.' });
      return;
    }
    handledReturnedRequestRef.current = true;
    setNotice({ tone: 'error', text: 'The returned request_id was not found in recent internal lab activity yet.' });
  }, [prefilledRequestId, recentRequests]);

  const tokenHeader = useMemo(() => decodeJwtPart(idToken, 0), [idToken]);
  const tokenPayload = useMemo(() => decodeJwtPart(idToken, 1), [idToken]);
  const flowIndicators = useMemo(() => {
    const identitySelected = Boolean(identityId.trim());
    const accountSelected = Boolean(accountId.trim());
    const loginCreated = Boolean(labRequest?.login_request_id);
    const returnedRequest = Boolean(prefilledRequestId && (labRequest?.login_request_id === prefilledRequestId || labRequest?.id === prefilledRequestId));
    const awaitingApproval = Boolean(labRequest && ['pending', 'login_request_created'].includes(labRequest.status));
    const approvedOrDenied = Boolean(labRequest && ['approved', 'denied'].includes(labRequest.status));
    const readyToFinalize = Boolean(labRequest && ['approved'].includes(labRequest.status));
    const claimsReady = Boolean(claimRelease || userinfo || idToken);
    const trailReady = recentRequests.length > 0;
    return [
      {
        title: 'Source verification',
        value: identitySelected ? 'Identity selected' : 'Choose an approved verification',
        detail: identitySelected ? identityId.trim() : 'Open /internal/identities to copy an identity_id or select an existing identity.',
        tone: identitySelected ? 'ready' : 'pending',
      },
      {
        title: 'idem identity',
        value: identitySelected ? 'Identity selected' : 'Identity not selected',
        detail: identitySelected ? 'Ready to anchor the lab flow.' : 'The lab needs an identity_id before it can start.',
        tone: identitySelected ? 'ready' : 'pending',
      },
      {
        title: 'Digital key account',
        value: accountSelected ? 'Account selected' : 'Account not selected',
        detail: accountSelected ? accountId.trim() : 'Use the identity detail page or digital key accounts page to copy the account_id.',
        tone: accountSelected ? 'ready' : 'pending',
      },
      {
        title: 'Login request created',
        value: loginCreated ? 'Created' : 'Not yet created',
        detail: loginCreated ? labRequest.login_request_id : 'Start the lab request after selecting identity and account.',
        tone: loginCreated ? 'ready' : 'neutral',
      },
      {
        title: 'Request returned',
        value: returnedRequest ? 'Returned from authenticator' : (hasReturnedRequest ? 'Waiting for request' : 'Not returned yet'),
        detail: returnedRequest ? `Request ${prefilledRequestId}` : (hasReturnedRequest ? 'The returned request_id has not been matched yet.' : 'Approve or deny a request in Authenticator Preview and return here.'),
        tone: returnedRequest ? 'ready' : (hasReturnedRequest ? 'active' : 'neutral'),
      },
      {
        title: 'Awaiting approval',
        value: awaitingApproval ? 'Yes' : 'Not pending',
        detail: awaitingApproval ? 'Open the authenticator preview to approve or deny the request.' : 'After approval the lab can be finalized and exchanged.',
        tone: awaitingApproval ? 'active' : 'neutral',
      },
      {
        title: 'Approved / denied',
        value: approvedOrDenied ? requestStatusLabel(labRequest.status) : 'Not finalized yet',
        detail: approvedOrDenied ? `Updated ${formatDate(labRequest.updated_at || labRequest.approved_at || labRequest.denied_at)}` : 'The request has not reached a terminal review decision.',
        tone: approvedOrDenied ? (labRequest.status === 'approved' ? 'ready' : 'pending') : 'neutral',
      },
      {
        title: 'Finalize',
        value: readyToFinalize ? 'Ready to finalize' : (labRequest && labRequest.status === 'denied' ? 'Denied request' : 'Not ready'),
        detail: readyToFinalize ? 'Use finalize to issue the short-lived lab code.' : 'Finalization becomes available after approval.',
        tone: readyToFinalize ? 'ready' : (labRequest && labRequest.status === 'denied' ? 'pending' : 'neutral'),
      },
      {
        title: 'Safe claims preview',
        value: claimsReady ? 'Available' : 'Not yet available',
        detail: claimsReady ? `${claimRelease?.allowed_claims?.length || 0} allowed · ${claimRelease?.blocked_claims?.length || 0} blocked` : 'Finalize the request and exchange the lab code to preview safe claims.',
        tone: claimsReady ? 'ready' : 'neutral',
      },
      {
        title: 'Audit trail',
        value: trailReady ? `${recentRequests.length} recent request(s)` : 'No trail yet',
        detail: trailReady ? 'Recent request rows below act as the safe event trail.' : 'Create the first request to populate the internal trail.',
        tone: trailReady ? 'ready' : 'neutral',
      },
    ];
  }, [accountId, claimRelease, hasReturnedRequest, identityId, idToken, labRequest, prefilledRequestId, recentRequests.length, userinfo]);

  const toggleClaim = (claim) => {
    setRequestedClaims((current) => current.includes(claim) ? current.filter((value) => value !== claim) : [...current, claim]);
  };

  const start = async (event) => {
    event.preventDefault();
    setLoading(true);
    setNotice(null);
    setAuthorizationCode('');
    setRedirectUri('');
    setIdToken('');
    setUserinfo(null);
    setClaimRelease(null);
    setConsumerRequestUrl('');
    try {
      const { data } = await api.post('/oidc-lab/admin/oidc-lab/start', {
        identity_id: identityId.trim(),
        digital_key_account_id: accountId.trim(),
        requested_assurance: requestedAssurance,
        requested_claim_keys: requestedClaims,
      });
      setLabRequest(data.request);
      setConsumerRequestUrl(data.consumer_request_url || (data.consumer_request_token ? `/idem/requests/${encodeURIComponent(data.consumer_request_token)}` : ''));
      setNotice({ tone: 'success', text: 'Lab login created. Open the consumer authenticator link, approve or deny it, then return here to finalize.' });
      await loadRecent();
    } catch (error) {
      setNotice({ tone: 'error', text: error?.response?.data?.error || 'The OIDC lab login could not be started.' });
    } finally {
      setLoading(false);
    }
  };

  const finalize = async () => {
    if (!labRequest) return;
    setLoading(true);
    setNotice(null);
    try {
      const { data } = await api.post(`/oidc-lab/admin/oidc-lab/requests/${labRequest.id}/finalize`);
      setLabRequest(data.request);
      setAuthorizationCode(data.authorization_code || '');
      setRedirectUri(data.simulated_redirect_uri || '');
      setNotice({ tone: 'success', text: 'A short-lived lab authorization code was issued. It is shown once and is not stored in raw form.' });
      await loadRecent();
    } catch (error) {
      setNotice({ tone: 'error', text: error?.response?.data?.error || 'The lab authorization could not be finalized yet.' });
    } finally {
      setLoading(false);
    }
  };

  const exchange = async () => {
    if (!authorizationCode) return;
    setLoading(true);
    setNotice(null);
    try {
      const { data } = await api.post('/oidc-lab/admin/oidc-lab/token', {
        grant_type: 'authorization_code',
        client_id: 'idemgo-rp-simulator',
        redirect_uri: '/internal/oidc-lab/callback',
        code: authorizationCode,
      });
      setLabRequest(data.request);
      setIdToken(data.id_token || '');
      setClaimRelease(data.claim_release || null);
      setNotice({ tone: 'success', text: 'Signed lab ID token issued. It is internal-only and becomes invalid after backend restart.' });
      await loadRecent();
    } catch (error) {
      setNotice({ tone: 'error', text: error?.response?.data?.error || 'The lab token exchange failed.' });
    } finally {
      setLoading(false);
    }
  };

  const fetchUserinfo = async () => {
    if (!labRequest) return;
    setLoading(true);
    setNotice(null);
    try {
      const { data } = await api.post('/oidc-lab/admin/oidc-lab/userinfo', { lab_request_id: labRequest.id });
      setUserinfo(data.userinfo || null);
      if (data.userinfo?.claim_release) setClaimRelease(data.userinfo.claim_release);
    } catch (error) {
      setNotice({ tone: 'error', text: error?.response?.data?.error || 'Lab userinfo could not be loaded.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="oidc-lab-simulator-page">
      <VerificationBreadcrumb rootLabel="Internal" rootTo="/internal" current="Private OIDC Lab" showDashboard={false}/>

      <div className="mb-6">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Platform</p>
        <h1 className="mt-1 font-display text-3xl font-bold tracking-tight">Private OIDC Lab</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-600">Internal relying-party simulator for idem Digital Key flows. This is not a public OIDC provider, FAS/CSAM integration, official LoA, or regulated identifier flow.</p>
      </div>

      <section className="border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-2"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5}/><div><p className="font-medium">Internal lab only</p><p className="mt-1">No public Login with idem, production OIDC provider, FAS/CSAM integration, official eIDAS LoA, RRN/BIS, or regulated identifier processing is present here.</p></div></div>
      </section>

      {notice && <div className={`mt-6 border px-4 py-3 text-sm ${notice.tone === 'success' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-red-300 bg-red-50 text-red-700'}`}>{notice.text}</div>}

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Internal flow status</h2>
            <p className="mt-1 text-sm text-zinc-600">This panel tracks the internal lab’s end-to-end state from selected identity to safe claims preview.</p>
          </div>
          <Link to="/internal/identities" className="text-sm text-zinc-700 underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">Open identities</Link>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {flowIndicators.map((item) => (
            <FlowCard key={item.title} {...item} />
          ))}
        </div>
      </section>

      <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex items-center gap-2"><KeyRound className="h-4 w-4 text-zinc-500" strokeWidth={1.5}/><div><h2 className="font-display text-lg font-semibold">Start login simulation</h2><p className="mt-1 text-sm text-zinc-600">Creates a linked internal Digital Key login request. Approval still requires a trusted device and the current internal assurance policy.</p></div></div>
        {hasPrefill && (
          <div className="mt-4 border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800">
            Pre-filled from digital key account. You can edit the identity and account values before starting the lab login.
          </div>
        )}
        {hasReturnedRequest && (
          <div className="mt-4 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
            Ready to finalize returned request when a matching login request is found.
          </div>
        )}
        <form onSubmit={start} className="mt-5 grid gap-4 xl:grid-cols-2">
          <label className="block text-sm text-zinc-700">Identity ID or public ID<input value={identityId} onChange={(event) => setIdentityId(event.target.value)} required placeholder="Paste an identity id or public id" className="mt-1 w-full border border-zinc-200 px-3 py-2 font-mono text-xs focus:border-zinc-500 focus:outline-none"/></label>
          <label className="block text-sm text-zinc-700">Digital Key Account ID<input value={accountId} onChange={(event) => setAccountId(event.target.value)} required placeholder="Paste a linked digital key account id" className="mt-1 w-full border border-zinc-200 px-3 py-2 font-mono text-xs focus:border-zinc-500 focus:outline-none"/></label>
          <label className="block text-sm text-zinc-700">Requested internal assurance<select value={requestedAssurance} onChange={(event) => setRequestedAssurance(event.target.value)} className="mt-1 w-full border border-zinc-200 bg-white px-3 py-2 text-sm focus:border-zinc-500 focus:outline-none"><option value="internal_foundation">Internal foundation</option><option value="internal_evidence_ready">Internal evidence ready</option><option value="internal_authentication_ready">Internal authentication ready</option></select></label>
          <fieldset className="border border-zinc-100 p-3"><legend className="px-1 text-xs font-mono uppercase tracking-widest text-zinc-500">Safe requested claims</legend><div className="mt-1 grid gap-2 sm:grid-cols-2">{labClaims.map(([claim, label]) => <label key={claim} className="flex items-center gap-2 text-xs text-zinc-700"><input type="checkbox" checked={requestedClaims.includes(claim)} onChange={() => toggleClaim(claim)} className="h-3.5 w-3.5 border-zinc-300 text-black focus:ring-0"/>{label}</label>)}</div></fieldset>
          <div className="xl:col-span-2"><button type="submit" disabled={loading} className="bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Start lab login</button></div>
        </form>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          <Link to="/internal/identities" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Open identities</Link>
          <Link to="/internal/digital-key-accounts" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Open digital key accounts</Link>
          <Link to="/internal/authenticator-preview" className="border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">Open authenticator preview</Link>
        </div>
        <p className="mt-4 text-xs text-zinc-500">
          Find <span className="font-mono">identity_id</span> and <span className="font-mono">account_id</span> on <Link to="/internal/identities" className="underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">the internal identities page</Link> or <Link to="/internal/digital-key-accounts" className="underline decoration-zinc-300 underline-offset-2 hover:decoration-zinc-900">the digital key accounts page</Link>. The lab stays internal-only and returns safe claims only.
        </p>
        {labRequest?.login_request_id && labRequest.status !== 'token_exchanged' && (
          <div className="mt-4 border border-sky-200 bg-sky-50 p-4 text-sm text-sky-900">
            <p className="font-medium">Next step: Review this request in the consumer authenticator</p>
            <p className="mt-1 text-sky-800">Request <span className="font-mono">{labRequest.login_request_id}</span> is ready for safe consumer approval or denial, then you can return here to finalize and preview safe claims.</p>
            <div className="mt-3">
              {consumerRequestUrl ? (
                <Link to={consumerRequestUrl} className="inline-flex items-center gap-1.5 border border-sky-200 bg-white px-3 py-2 text-sky-800 hover:border-sky-300">
                  <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5}/>
                  Open consumer authenticator
                </Link>
              ) : null}
              <Link to={`/internal/authenticator-preview?request_id=${encodeURIComponent(labRequest.login_request_id)}`} className="ml-2 inline-flex items-center gap-1.5 border border-zinc-200 bg-white px-3 py-2 text-zinc-700 hover:border-zinc-400">
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5}/>
                Open internal authenticator preview
              </Link>
            </div>
          </div>
        )}
      </section>

      {labRequest && <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3"><div><h2 className="font-display text-lg font-semibold">Lab authorization</h2><p className="mt-1 text-sm text-zinc-600">Request {labRequest.id}</p></div><LabStatus status={labRequest.status}/></div>
        <dl className="mt-4 grid gap-3 text-sm md:grid-cols-3"><div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Linked login request</dt><dd className="mt-1 font-mono text-xs text-zinc-700">{labRequest.login_request_id || '—'}</dd></div><div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested assurance</dt><dd className="mt-1 text-zinc-700">{labRequest.requested_assurance?.replaceAll('_', ' ')}</dd></div><div><dt className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">State</dt><dd className="mt-1 font-mono text-xs text-zinc-700">{labRequest.state || '—'}</dd></div></dl>
        {!authorizationCode && labRequest.status !== 'token_exchanged' && <div className="mt-4 flex flex-wrap items-center gap-2"><Link to="/id/authenticator" className="inline-flex items-center gap-1.5 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400">Open idem authenticator <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5}/></Link><button type="button" onClick={finalize} disabled={loading} className="border border-emerald-300 px-3 py-2 text-sm text-emerald-700 hover:border-emerald-500 disabled:opacity-50">Finalize and issue lab code</button></div>}
        {authorizationCode && <div className="mt-4 border-t border-zinc-100 pt-4"><p className="text-sm font-medium text-zinc-800">Lab code issued</p><p className="mt-1 text-xs text-zinc-600">Expires {formatDate(labRequest.code_expires_at)}. The raw code is only kept in this browser state for this exchange.</p><p className="mt-3 break-all border border-zinc-100 bg-zinc-50 p-3 font-mono text-xs text-zinc-700">{redirectUri}</p><details className="mt-3"><summary className="cursor-pointer text-xs text-zinc-600">Show raw lab authorization code</summary><p className="mt-2 break-all border border-amber-200 bg-amber-50 p-3 font-mono text-xs text-amber-900">{authorizationCode}</p></details><button type="button" onClick={exchange} disabled={loading} className="mt-4 bg-black px-3 py-2 text-sm text-white hover:bg-zinc-800 disabled:opacity-50">Exchange code for lab token</button></div>}
      </section>}

      {idToken && <section className="mt-6 border border-zinc-200 bg-white p-5"><h2 className="font-display text-lg font-semibold">Lab token result</h2><p className="mt-1 text-sm text-zinc-600">Signed with the current ephemeral lab key. This is not a production credential and becomes invalid after backend restart.</p><div className="mt-4 grid gap-4 xl:grid-cols-2"><pre className="overflow-x-auto border border-zinc-100 bg-zinc-50 p-3 text-xs text-zinc-700">{JSON.stringify(tokenHeader, null, 2)}</pre><pre className="overflow-x-auto border border-zinc-100 bg-zinc-50 p-3 text-xs text-zinc-700">{JSON.stringify(tokenPayload, null, 2)}</pre></div><details className="mt-4"><summary className="cursor-pointer text-xs text-zinc-600">Show raw lab ID token</summary><p className="mt-2 break-all border border-amber-200 bg-amber-50 p-3 font-mono text-xs text-amber-900">{idToken}</p></details><button type="button" onClick={fetchUserinfo} disabled={loading} className="mt-4 border border-zinc-200 px-3 py-2 text-sm text-zinc-700 hover:border-zinc-400 disabled:opacity-50">Fetch lab userinfo</button></section>}

      {claimRelease && <section className="mt-6 border border-zinc-200 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold">Safe claims preview</h2>
            <p className="mt-1 text-sm text-zinc-600">Consent-gated preview for the fixed internal RP simulator. No raw claim values, identity evidence, regulated identifiers, documents, addresses, or dates of birth are released.</p>
          </div>
          <div className="flex flex-wrap gap-2 text-xs text-zinc-600">
            <span className="border border-emerald-200 bg-emerald-50 px-2 py-1">{(claimRelease.allowed_claims || []).length} allowed</span>
            <span className="border border-amber-200 bg-amber-50 px-2 py-1">{(claimRelease.blocked_claims || []).length} blocked</span>
            <span className="border border-zinc-200 bg-zinc-50 px-2 py-1">{claimRelease.consent?.id || 'No consent match'}</span>
          </div>
        </div>
        <div className="mt-4 grid gap-4 xl:grid-cols-3">
          <div className="border border-zinc-100 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Requested claims</p>
            <div className="mt-2 flex flex-wrap gap-1.5">{(claimRelease.requested_claims || []).length ? claimRelease.requested_claims.map((claim) => <span key={claim} className="border border-zinc-200 bg-white px-2 py-1 font-mono text-[10px] text-zinc-600">{claim}</span>) : <span className="text-xs text-zinc-500">No claims requested</span>}</div>
          </div>
          <div className="border border-emerald-100 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-700">Allowed claims</p>
            <div className="mt-2 flex flex-wrap gap-1.5">{(claimRelease.allowed_claims || []).length ? claimRelease.allowed_claims.map((claim) => <span key={claim} className="border border-emerald-200 bg-white px-2 py-1 font-mono text-[10px] text-emerald-700">{claim}</span>) : <span className="text-xs text-zinc-500">No claims allowed</span>}</div>
          </div>
          <div className="border border-amber-100 p-3">
            <p className="font-mono text-[10px] uppercase tracking-widest text-amber-700">Blocked claims</p>
            <div className="mt-2 space-y-1.5">{(claimRelease.blocked_claims || []).length ? claimRelease.blocked_claims.map((claim) => <div key={`${claim.claim_name}-${claim.reason}`} className="flex items-center justify-between gap-2 border border-amber-100 bg-white px-2 py-1"><span className="font-mono text-[10px] text-zinc-700">{claim.claim_name}</span><span className="text-[10px] text-amber-700">{String(claim.reason || 'blocked').replaceAll('_', ' ')}</span></div>) : <span className="text-xs text-zinc-500">No claims blocked</span>}</div>
          </div>
        </div>
        {claimRelease.consent && <p className="mt-3 text-xs text-zinc-500">Matched internal consent: <span className="font-mono">{claimRelease.consent.id}</span> · {claimRelease.consent.consent_status}</p>}
      </section>}

      {userinfo && <section className="mt-6 border border-zinc-200 bg-white p-5"><h2 className="font-display text-lg font-semibold">Lab userinfo</h2><pre className="mt-4 overflow-x-auto border border-zinc-100 bg-zinc-50 p-3 text-xs text-zinc-700">{JSON.stringify(userinfo, null, 2)}</pre></section>}

      <section className="mt-6 border border-zinc-200 bg-white p-5"><h2 className="font-display text-lg font-semibold">Recent audit / event trail</h2><p className="mt-1 text-sm text-zinc-600">These rows are derived from safe request status metadata only. No raw evidence, tokens, or secrets are shown.</p><div className="mt-4 overflow-x-auto"><table className="min-w-full text-sm"><thead className="border-b border-zinc-100 text-left font-mono text-[10px] uppercase tracking-widest text-zinc-500"><tr><th className="py-2 pr-3 font-medium">Lab request</th><th className="py-2 pr-3 font-medium">Identity</th><th className="py-2 pr-3 font-medium">Status</th><th className="py-2 pr-3 font-medium">Assurance</th><th className="py-2 pr-3 font-medium">Login request</th><th className="py-2 pr-3 font-medium">Updated</th></tr></thead><tbody>{recentRequests.map((request) => <tr key={request.id} className="border-b border-zinc-100 last:border-b-0"><td className="max-w-48 truncate py-3 pr-3 font-mono text-xs text-zinc-700"><button type="button" onClick={() => { setLabRequest(request); setAuthorizationCode(''); setIdToken(''); setUserinfo(null); setClaimRelease(null); }} className="hover:underline" title={request.id}>{request.id}</button></td><td className="py-3 pr-3 text-xs text-zinc-600"><div>{request.identity_display_name || request.identity_ref}</div><div className="mt-1 font-mono text-[10px] text-zinc-500">{request.identity_id || '—'}</div></td><td className="py-3 pr-3"><LabStatus status={request.status}/></td><td className="py-3 pr-3 text-xs text-zinc-600">{request.requested_assurance?.replaceAll('_', ' ')}</td><td className="py-3 pr-3 font-mono text-xs text-zinc-600">{request.login_request_id || '—'}</td><td className="whitespace-nowrap py-3 text-xs text-zinc-500">{formatDate(request.updated_at || request.created_at)}</td></tr>)}</tbody></table></div></section>
    </div>
  );
}
